import { useState, FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { authApi } from '@/api/auth'
import { subscriptionApi } from '@/api/subscription'
import { useAuthStore } from '@/store/authStore'
import { handleApiError } from '@/api/client'
import type { LoginRequest } from '@/types/api'

export function LoginPage() {
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false)

  const loginMutation = useMutation({
    mutationFn: (data: LoginRequest) => authApi.login(data),
    onSuccess: async (data) => {
      login(data.user, data.accessToken, data.refreshToken)

      // 구독 상태 체크 후 적절한 페이지로 이동
      setIsCheckingSubscription(true)
      try {
        const subscription = await subscriptionApi.checkMySubscription()
        if (subscription.hasActiveSubscription) {
          navigate('/learn', { replace: true })
        } else {
          navigate('/subscription/plans', { replace: true })
        }
      } catch {
        // API 에러 시에도 구독 페이지로 이동
        navigate('/subscription/plans', { replace: true })
      } finally {
        setIsCheckingSubscription(false)
      }
    },
    onError: (error) => {
      setError(handleApiError(error))
    },
  })

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    
    // 간단한 유효성 검사
    if (!email || !email.includes('@')) {
      setError('올바른 이메일을 입력하세요')
      return
    }
    if (!password) {
      setError('비밀번호를 입력하세요')
      return
    }
    
    setError(null)
    loginMutation.mutate({ email, password })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-primary-600">Quebon</h1>
          <h2 className="mt-6 text-2xl font-semibold text-gray-900">로그인</h2>
          <p className="mt-2 text-gray-600">계정에 로그인하세요</p>
        </div>

        <div className="card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="label">이메일</label>
              <input
                id="email"
                type="text"
                autoComplete="email"
                className="input"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="password" className="label">비밀번호</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                className="input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <button
              type="submit"
              disabled={loginMutation.isPending || isCheckingSubscription}
              className="w-full btn btn-primary btn-lg"
            >
              {loginMutation.isPending ? '로그인 중...' : isCheckingSubscription ? '구독 확인 중...' : '로그인'}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            계정이 없으신가요?{' '}
            <Link to="/register" className="text-primary-600 hover:underline font-medium">
              회원가입
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}


